import {
  renderFormattedDate,
  summarizeProductBanks,
  getProductBankLimitState,
  renderDataGridLocaleText,
  normalizeProductBankLimit,
  formatProjectCompanyCodes,
  normalizeProjectCompanyCodes,
} from 'utils';
import {
  StyledBox,
  CustomToolbar,
  ZebraDataGrid,
  GridEditDateCell,
  ProductBanksDialog,
  EditToolbarProducts,
  ProductCompanyCodesCell,
  UserAutocompleteEditCell,
  ProductCompanyCodesDialog,
  getProductCompanyStatusLabel,
} from 'components';
import dayjs from 'dayjs';
import {
  GridRowId,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowsProp,
  GridColTypeDef,
  GridEventListener,
  GridRowModesModel,
  GridValidRowModel,
  GRID_DATE_COL_DEF,
  GridActionsCellItem,
  GridRenderCellParams,
  getGridDateOperators,
  GridRowEditStopReasons,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-premium';
import { useIntl } from 'react-intl';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { CompanyToBeEdited } from 'pages/dashboard';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM } from 'enums';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useTheme, alpha, Chip, Box, IconButton, Tooltip } from '@mui/material';
import { EnvironmentType, Project, User, UsedBank, ProjectCompanyCode, ProjectCompanyCodeBank } from 'library/types';

type ProductsEditTableProps = {
  rows: GridRowsProp;
  setRows: Dispatch<SetStateAction<readonly GridValidRowModel[]>>;
  rowModesModel: GridRowModesModel;
  setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
  products: Project[];
  users: User[];
  environmentTypes: EnvironmentType[];
  editMode: boolean;
  completedProjectIds: number[];
  companies?: readonly CompanyToBeEdited[];
  usedBanks?: UsedBank[];
};

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  renderEditCell: (params) => {
    const v = params.value;

    const dayjsValue = v instanceof Date ? dayjs(v) : v ? dayjs(v) : null;

    return <GridEditDateCell {...params} value={dayjsValue} />;
  },
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponentProps: { showTime: false },
  })),
};

const ProductsEditTable = ({
  rows,
  setRows,
  rowModesModel,
  setRowModesModel,
  products,
  users,
  environmentTypes,
  editMode,
  completedProjectIds,
  companies = [],
  usedBanks = [],
}: ProductsEditTableProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();

  const formatCompanyCodes = (value: unknown) =>
    formatProjectCompanyCodes(value, (status) => getProductCompanyStatusLabel(status, intl));

  // dialog states
  const [companyCodesDialog, setCompanyCodesDialog] = useState<{
    open: boolean;
    rowId: GridRowId | null;
    companyCodes: ProjectCompanyCode[];
    productTitle: string;
    editable: boolean;
  }>({
    open: false,
    rowId: null,
    companyCodes: [],
    productTitle: '',
    editable: false,
  });

  const [banksDialog, setBanksDialog] = useState<{
    open: boolean;
    rowId: GridRowId | null;
    banks: ProjectCompanyCodeBank[];
    bankCount: number;
    availableCompanyCodes: ProjectCompanyCode[];
    productTitle: string;
    editable: boolean;
  }>({
    open: false,
    rowId: null,
    banks: [],
    bankCount: 0,
    availableCompanyCodes: [],
    productTitle: '',
    editable: false,
  });

  const priorityOptions = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: intl.formatMessage({ id: 'enum.projectStepPriorityType.nonUrgent', defaultMessage: 'Düşük' }),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: intl.formatMessage({ id: 'enum.projectStepPriorityType.urgent', defaultMessage: 'Orta' }),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: intl.formatMessage({ id: 'enum.projectStepPriorityType.veryUrgent', defaultMessage: 'Yüksek' }),
      },
    ],
    [intl]
  );

  const isCompletedProduct = (row: GridValidRowModel) => {
    const productId = Number(row.projectId);

    return Number.isFinite(productId) && completedProjectIds.includes(productId);
  };

  const getProductTitleFromRow = (row: GridValidRowModel) => {
    if (row.projectId) {
      const found = products.find((p) => p.id === Number(row.projectId) || p.title === row.projectId);

      if (found) return found.title;
    }

    return '';
  };

  const handleOpenCompanyCodesModal = (params: GridRenderCellParams) => {
    const latestRow = params.api.getRowWithUpdatedValues(params.id, params.field);
    const codes = normalizeProjectCompanyCodes(latestRow.companyCodes);
    setCompanyCodesDialog({
      open: true,
      rowId: params.id,
      companyCodes: codes,
      productTitle: getProductTitleFromRow(latestRow),
      editable: editMode,
    });
  };

  const handleSaveCompanyCodesFromDialog = (updatedCodes: ProjectCompanyCode[]) => {
    if (companyCodesDialog.rowId == null) return;
    setRows((prevRows) =>
      prevRows.map((r) =>
        r.id === companyCodesDialog.rowId ? { ...r, companyCodes: normalizeProjectCompanyCodes(updatedCodes) } : r
      )
    );
  };

  const handleOpenBanksModal = (params: GridRenderCellParams) => {
    const latestRow = params.api.getRowWithUpdatedValues(params.id, 'bankCount');
    const rowBanks: ProjectCompanyCodeBank[] = Array.isArray(latestRow.banks) ? latestRow.banks : [];
    const count = Number(latestRow.bankCount) || 0;
    // Row's own companyCodes: strictly watched
    const rowCompanyCodes = normalizeProjectCompanyCodes(latestRow.companyCodes);

    setBanksDialog({
      open: true,
      rowId: params.id,
      banks: rowBanks,
      bankCount: count,
      availableCompanyCodes: rowCompanyCodes,
      productTitle: getProductTitleFromRow(latestRow),
      editable: editMode,
    });
  };

  const handleSaveBanksFromDialog = (updatedBanks: ProjectCompanyCodeBank[]) => {
    if (banksDialog.rowId == null) return;

    setRows((prevRows) => prevRows.map((r) => (r.id === banksDialog.rowId ? { ...r, banks: updatedBanks } : r)));
  };

  // dynamic variables
  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      editable: false,
    },
    {
      field: 'projectId',
      headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
      minWidth: 180,
      editable: editMode,
      type: 'singleSelect',
      valueOptions: () => products.map((product) => ({ value: product.id, label: product.title })),
    },
    {
      field: 'companyCodes',
      headerName: intl.formatMessage({ id: 'project.table.companyCodes', defaultMessage: 'Şirket Kodları' }),
      minWidth: 260,
      align: 'center',
      headerAlign: 'center',
      editable: false,
      valueGetter: (_value, row) => formatCompanyCodes(row.companyCodes),
      renderCell: (params: GridRenderCellParams) => (
        <ProductCompanyCodesCell
          value={params.row.companyCodes}
          editable={editMode}
          onClick={() => handleOpenCompanyCodesModal(params)}
        />
      ),
    },
    {
      field: 'bankCount',
      headerName: intl.formatMessage({ id: 'project.table.bankCount', defaultMessage: 'Banka Limiti' }),
      type: 'number',
      editable: editMode,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value !== null && value !== undefined ? value : 0),
      preProcessEditCellProps: (params) => ({
        ...params.props,
        error: normalizeProductBankLimit(params.props.value) === null,
      }),
    },
    {
      field: 'banks',
      headerName: intl.formatMessage({ id: 'project.table.banks', defaultMessage: 'Bankalar' }),
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const banksList: ProjectCompanyCodeBank[] = Array.isArray(params.value) ? params.value : [];
        const bankSummary = summarizeProductBanks(banksList);
        const totalBanks = bankSummary.assignmentCount;
        const bankLimitUsageCount = bankSummary.bankLimitUsageCount;
        const limit = normalizeProductBankLimit(params.row.bankCount);
        const limitState = getProductBankLimitState(params.row.bankCount, bankLimitUsageCount);
        const bankLimitUsageDescription = intl.formatMessage(
          {
            id: 'project.table.bankLimitUsage',
            defaultMessage: 'Banka limiti: {current} / {limit} pasif olmayan banka · Toplam {total} atama',
          },
          { current: bankLimitUsageCount, limit: limit ?? '?', total: totalBanks }
        );
        const bankActionLabel = intl.formatMessage({
          id: editMode ? 'project.table.manageBanks' : 'project.table.showBanks',
          defaultMessage: editMode ? 'Bankaları Yönet' : 'Bankaları Göster',
        });
        const bankLimitActionDescription = `${bankActionLabel}: ${bankLimitUsageDescription}`;

        return (
          <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%" gap={1}>
            <Tooltip title={bankActionLabel}>
              <IconButton size="small" sx={{ border: 'none', p: 0.5 }} onClick={() => handleOpenBanksModal(params)}>
                <AccountBalanceIcon fontSize="small" color={totalBanks > 0 ? 'primary' : 'action'} />
              </IconButton>
            </Tooltip>
            <Tooltip title={bankLimitActionDescription}>
              <Chip
                label={`${bankLimitUsageCount} / ${limit ?? '?'}`}
                aria-label={bankLimitActionDescription}
                size="small"
                variant={totalBanks > 0 ? 'filled' : 'outlined'}
                color={
                  limitState === 'invalid' || limitState === 'exceeded'
                    ? 'error'
                    : limitState === 'reached'
                      ? 'warning'
                      : bankLimitUsageCount > 0
                        ? 'primary'
                        : 'default'
                }
                sx={{
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 22,
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: 1, overflow: 'visible' },
                }}
                onClick={() => handleOpenBanksModal(params)}
              />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'environmentTypeId',
      headerName: intl.formatMessage({ id: 'project.table.environmentType', defaultMessage: 'Sunucu Tipi' }),
      minWidth: 140,
      editable: editMode,
      type: 'singleSelect',
      valueOptions: () =>
        environmentTypes.map((environmentType) => ({ value: environmentType.id, label: environmentType.title })),
    },
    {
      field: 'liveDate',
      ...dateColumnType,
      valueGetter: (value) => {
        if (!value) return null;

        return new Date(value);
      },
      valueParser: (value) => {
        if (!value) return null;

        return new Date(value);
      },
      valueFormatter: (value) => {
        return value ? renderFormattedDate(value) : '';
      },
      headerName: intl.formatMessage({ id: 'project.table.liveDate', defaultMessage: 'Canlı Geçiş' }),
      minWidth: 140,
      editable: editMode,
    },
    {
      field: 'priority',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.priority',
        defaultMessage: 'Öncelik',
      }),
      headerAlign: 'center',
      align: 'center',
      type: 'singleSelect',
      editable: editMode,
      valueOptions: priorityOptions.map((x) => x.value),
      getOptionLabel: (value) => priorityOptions.find((x) => x.value === value)?.label ?? String(value),
      valueFormatter: (value) => priorityOptions.find((x) => x.value === value)?.label ?? '',
      renderCell: (params) => {
        if (!params.value) return null;

        const priority = params.value;
        const colorKey =
          priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
            ? 'success'
            : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
              ? 'warning'
              : 'error';

        return (
          <Chip
            size="small"
            label={priorityOptions.find((x) => x.value === priority)?.label ?? ''}
            sx={{
              fontWeight: 600,
              bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
              color: theme.palette[colorKey].main,
              border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
            }}
          />
        );
      },
      minWidth: 140,
    },
    {
      field: 'isOnHold',
      headerName: intl.formatMessage({
        id: 'project.table.isOnHold',
        defaultMessage: 'Beklemede',
      }),
      type: 'boolean',
      editable: editMode,
      minWidth: 110,
    },
    {
      field: 'onHoldTotalDays',
      headerName: intl.formatMessage({
        id: 'project.table.onHoldTotalDays',
        defaultMessage: 'Beklemede Gün',
      }),
      type: 'number',
      editable: false,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => (value !== null && value !== undefined ? value : 0),
    },
    {
      field: 'relatedContactId',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.relatedContact',
        defaultMessage: 'İlgili Kişisi',
      }),
      headerAlign: 'center',
      align: 'center',
      editable: editMode,
      valueFormatter: (value) => users.find((user) => user.id === value)?.nameSurname ?? '',
      renderEditCell: (params: GridRenderEditCellParams) => <UserAutocompleteEditCell {...params} users={users} />,
      minWidth: 180,
    },
    {
      field: 'alternativeRelatedContactId',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.alternativeRelatedContact',
        defaultMessage: 'Yedek İlgili Kişisi',
      }),
      headerAlign: 'center',
      align: 'center',
      editable: editMode,
      valueFormatter: (value) => users.find((user) => user.id === value)?.nameSurname ?? '',
      renderEditCell: (params: GridRenderEditCellParams) => <UserAutocompleteEditCell {...params} users={users} />,
      minWidth: 200,
    },
    {
      field: 'notes',
      headerName: intl.formatMessage({
        id: 'projectStepManaging.notes',
        defaultMessage: 'Notlar',
      }),
      editable: editMode,
      minWidth: 260,
    },
    editMode
      ? {
          field: 'actions',
          type: 'actions',
          headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
          width: 100,
          cellClassName: 'actions',
          getActions: ({ id, row }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label={intl.formatMessage({ id: 'project.table.save', defaultMessage: 'Kaydet' })}
                  sx={{
                    color: 'primary.main',
                  }}
                  onClick={handleSaveClick(id)}
                />,
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label={intl.formatMessage({ id: 'project.table.cancel', defaultMessage: 'Vazgeç' })}
                  className="textPrimary"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />,
              ];
            }

            return [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={intl.formatMessage({ id: 'project.table.edit', defaultMessage: 'Düzenle' })}
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={intl.formatMessage({ id: 'project.table.delete', defaultMessage: 'Sil' })}
                disabled={isCompletedProduct(row)}
                onClick={handleDeleteClick(id)}
                color="inherit"
              />,
            ];
          },
        }
      : {
          field: 'none',
        },
  ];

  // sync helper functions
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = {
      ...newRow,
      isNew: false,
      companyCodes: normalizeProjectCompanyCodes(newRow.companyCodes),
      bankCount: Number(newRow.bankCount) || 0,
      banks: Array.isArray(newRow.banks) ? newRow.banks : [],
    };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <>
      <ProductCompanyCodesDialog
        open={companyCodesDialog.open}
        onClose={() => setCompanyCodesDialog((prev) => ({ ...prev, open: false }))}
        companyCodes={companyCodesDialog.companyCodes}
        allCompanies={companies}
        productTitle={companyCodesDialog.productTitle}
        editable={companyCodesDialog.editable}
        onSave={handleSaveCompanyCodesFromDialog}
      />

      <ProductBanksDialog
        open={banksDialog.open}
        onClose={() => setBanksDialog((prev) => ({ ...prev, open: false }))}
        banks={banksDialog.banks}
        bankCount={banksDialog.bankCount}
        availableCompanyCodes={banksDialog.availableCompanyCodes}
        usedBanks={usedBanks}
        editable={banksDialog.editable}
        productTitle={banksDialog.productTitle}
        onSave={handleSaveBanksFromDialog}
      />

      <StyledBox sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
        <ZebraDataGrid
          key={editMode ? 'edit' : 'view'}
          rows={rows}
          columns={columns}
          columnVisibilityModel={{ none: false }}
          disableColumnMenu
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          isCellEditable={(params) => {
            if (!editMode) return false;
            if (params.field === 'environmentTypeId' || params.field === 'bankCount') {
              return true;
            }

            return !isCompletedProduct(params.row);
          }}
          localeText={renderDataGridLocaleText(intl)}
          initialState={{
            pinnedColumns: editMode ? { right: ['actions'] } : {},
          }}
          slots={{
            toolbar: () => (
              <>
                <EditToolbarProducts
                  setRowModesModel={setRowModesModel}
                  setRows={setRows}
                  showButton={editMode && rows.length < products.length}
                />
                {!editMode && <CustomToolbar exportPremiumExcel={true} />}
              </>
            ),
          }}
          slotProps={{
            toolbar: { setRows, setRowModesModel },
          }}
          hideFooter
          disableRowSelectionOnClick
          disableColumnSorting
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            border: `1px ${theme.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
          }}
          disableAggregation
          disableRowGrouping
        />
      </StyledBox>
    </>
  );
};

export default ProductsEditTable;
